import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WorkOrderCard = ({ 
  workOrder, 
  onExpand, 
  isExpanded, 
  onStartQuotation, 
  onContactClient,
  onSendToInspection 
}) => {
  const statusConfig = {
    'recepcionado': { 
      label: 'Recepcionado', 
      color: 'text-warning', 
      bgColor: 'bg-warning/10',
      icon: 'Clock'
    },
    'inspeccionado': { 
      label: 'Inspeccionado', 
      color: 'text-primary', 
      bgColor: 'bg-primary/10',
      icon: 'CheckCircle'
    },
    'cotizando': { 
      label: 'Cotizando', 
      color: 'text-accent', 
      bgColor: 'bg-accent/10',
      icon: 'FileText'
    },
    'aprobado': { 
      label: 'Aprobado', 
      color: 'text-success', 
      bgColor: 'bg-success/10',
      icon: 'CheckCircle2'
    }
  };

  const status = statusConfig?.[workOrder?.estadoOT] || statusConfig?.recepcionado;

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden transition-smooth hover:shadow-elevation-2">
      <div 
        className="p-4 md:p-6 cursor-pointer"
        onClick={() => onExpand(workOrder?.id)}
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
            <div className={`p-2 md:p-3 rounded-lg ${status?.bgColor} flex-shrink-0`}>
              <Icon name={status?.icon} size={24} className={status?.color} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                  {workOrder?.folioOT}
                </h3>
                <span className={`inline-flex items-center gap-1.5 px-2 md:px-3 py-1 rounded-md text-xs md:text-sm font-body font-medium ${status?.bgColor} ${status?.color} w-fit`}>
                  {status?.label}
                </span>
              </div>
              
              <div className="space-y-1.5 md:space-y-2">
                <div className="flex items-center gap-2 text-sm md:text-base">
                  <Icon name="Car" size={16} className="text-muted-foreground flex-shrink-0" />
                  <span className="font-body font-medium text-foreground truncate">
                    {workOrder?.unidad?.marca} {workOrder?.unidad?.modelo} {workOrder?.unidad?.año}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-xs md:text-sm caption text-muted-foreground">
                  <Icon name="Hash" size={14} className="flex-shrink-0" />
                  <span className="truncate">{workOrder?.unidad?.placas || workOrder?.unidad?.vin}</span>
                </div>
                
                <div className="flex items-center gap-2 text-xs md:text-sm caption text-muted-foreground">
                  <Icon name="Calendar" size={14} className="flex-shrink-0" />
                  <span>Ingreso: {new Date(workOrder.fechaIngreso)?.toLocaleDateString('es-MX')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 lg:flex-col lg:items-end">
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={20} 
              className="text-muted-foreground transition-smooth"
            />
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-border bg-muted/30 p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-3 md:space-y-4">
              <h4 className="text-sm md:text-base font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="Car" size={18} className="text-primary" />
                Datos del Vehículo
              </h4>
              
              <div className="bg-card rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
                <div className="grid grid-cols-2 gap-2 md:gap-3 text-xs md:text-sm">
                  <div>
                    <span className="caption text-muted-foreground block mb-1">Tipo</span>
                    <span className="font-body font-medium text-foreground">{workOrder?.unidad?.tipo}</span>
                  </div>
                  <div>
                    <span className="caption text-muted-foreground block mb-1">Color</span>
                    <span className="font-body font-medium text-foreground">{workOrder?.unidad?.color}</span>
                  </div>
                  <div>
                    <span className="caption text-muted-foreground block mb-1">Motor</span>
                    <span className="font-body font-medium text-foreground">{workOrder?.unidad?.motor}</span>
                  </div>
                  <div>
                    <span className="caption text-muted-foreground block mb-1">Kilometraje</span>
                    <span className="font-body font-medium text-foreground">{workOrder?.unidad?.kilometraje?.toLocaleString('es-MX')} km</span>
                  </div>
                </div>
                
                <div className="pt-2 md:pt-3 border-t border-border">
                  <span className="caption text-muted-foreground block mb-1 text-xs md:text-sm">VIN</span>
                  <span className="font-mono text-xs md:text-sm text-foreground break-all">{workOrder?.unidad?.vin}</span>
                </div>
              </div>
            </div>

            {workOrder?.inspeccion && (
              <div className="space-y-3 md:space-y-4">
                <h4 className="text-sm md:text-base font-heading font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Search" size={18} className="text-accent" />
                  Hallazgos de Inspección
                </h4>
                
                <div className="bg-card rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
                  {workOrder?.inspeccion?.frenos?.hallazgos && (
                    <div className="pb-2 md:pb-3 border-b border-border">
                      <div className="flex items-start gap-2 mb-1">
                        <Icon name="AlertCircle" size={14} className="text-error mt-0.5 flex-shrink-0" />
                        <span className="text-xs md:text-sm font-body font-medium text-foreground">Frenos</span>
                      </div>
                      <p className="text-xs md:text-sm caption text-muted-foreground ml-6">
                        {workOrder?.inspeccion?.frenos?.hallazgos}
                      </p>
                    </div>
                  )}
                  
                  {workOrder?.inspeccion?.suspension?.hallazgos && (
                    <div className="pb-2 md:pb-3 border-b border-border">
                      <div className="flex items-start gap-2 mb-1">
                        <Icon name="AlertCircle" size={14} className="text-warning mt-0.5 flex-shrink-0" />
                        <span className="text-xs md:text-sm font-body font-medium text-foreground">Suspensión</span>
                      </div>
                      <p className="text-xs md:text-sm caption text-muted-foreground ml-6">
                        {workOrder?.inspeccion?.suspension?.hallazgos}
                      </p>
                    </div>
                  )}
                  
                  {workOrder?.inspeccion?.afinacion?.hallazgos && (
                    <div>
                      <div className="flex items-start gap-2 mb-1">
                        <Icon name="AlertCircle" size={14} className="text-accent mt-0.5 flex-shrink-0" />
                        <span className="text-xs md:text-sm font-body font-medium text-foreground">Afinación</span>
                      </div>
                      <p className="text-xs md:text-sm caption text-muted-foreground ml-6">
                        {workOrder?.inspeccion?.afinacion?.hallazgos}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {workOrder?.estadoFisico && workOrder?.estadoFisico?.fotos && workOrder?.estadoFisico?.fotos?.length > 0 && (
            <div className="space-y-3 md:space-y-4">
              <h4 className="text-sm md:text-base font-heading font-semibold text-foreground flex items-center gap-2">
                <Icon name="Camera" size={18} className="text-secondary" />
                Evidencia Fotográfica
              </h4>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
                {workOrder?.estadoFisico?.fotos?.slice(0, 4)?.map((foto, index) => (
                  <div key={index} className="aspect-square rounded-lg overflow-hidden bg-muted">
                    <Image
                      src={foto?.url}
                      alt={foto?.alt}
                      className="w-full h-full object-cover hover:scale-105 transition-smooth"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-3 md:pt-4 border-t border-border">
            <button
              onClick={() => onStartQuotation(workOrder)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:py-3 rounded-lg bg-primary text-primary-foreground font-body font-medium text-sm md:text-base transition-smooth hover-lift press-effect focus-ring shadow-elevation-1 hover:shadow-elevation-2"
            >
              <Icon name="FileText" size={18} />
              <span>Iniciar Cotización</span>
            </button>
            
            <button
              onClick={() => onSendToInspection(workOrder)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:py-3 rounded-lg bg-accent text-accent-foreground font-body font-medium text-sm md:text-base transition-smooth hover-lift press-effect focus-ring shadow-elevation-1 hover:shadow-elevation-2"
            >
              <Icon name="ClipboardCheck" size={18} />
              <span>Enviar a Inspección</span>
            </button>
            
            <button
              onClick={() => onContactClient(workOrder)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:py-3 rounded-lg bg-secondary text-secondary-foreground font-body font-medium text-sm md:text-base transition-smooth hover-lift press-effect focus-ring shadow-elevation-1 hover:shadow-elevation-2"
            >
              <Icon name="Phone" size={18} />
              <span>Contactar Cliente</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkOrderCard;